import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import MiscUtils from 'services/MiscUtils';
import View from './view';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';

const PatientInfo = (props) => {
  const dispatch = useAppDispatch();

  const authentication = useAppSelector((state) => state.authentication);
  const clinics = useAppSelector((state) => state.resource.clinics);

  /** @type {import('../../../generated/api').Patient} */
  const patient = props.patient;

  const [submitting, setSubmitting] = useState(false);
  const [resendingEmailConfirmation, setResendingEmailConfirmation] = useState(false);
  const [username, setUsername] = useState();
  const [value, setValue] = useState({ ...patient });
  const [usernameNotFound, setUsernameNotFound] = useState(false);

  const ownRole = authentication.role;
  const ownId = authentication.activeUserId;

  const allowEdit = ownRole === 'admin' || ownId === patient.id;
  const patientEdited = !_.isEqual(patient, value);

  async function onGenerateCredentials() {
    let result = await dispatch(
      api.Authentication.generateCredentialsAuthenticationCode({ patientId: patient.id }),
      {
        errorTitle: 'Generate Credentials',
      },
    );
    if (result) {
      MiscUtils.showToastrInfo({ title: 'Patient Authentication Code', message: result.value });
    }
  }

  async function onShowUsername() {
    let result = await dispatch(api.Authentication.getUsername({ userId: patient.id }), {
      errorTitle: 'Username',
    });
    if (result) {
      setUsername(result.value);
    } else {
      setUsernameNotFound(true);
    }
  }

  function onEmergencyContactChange(event) {
    setValue((prevState) => {
      return {
        ...prevState,
        emergency_contact: {
          ...(prevState.emergency_contact || {}),
          [event.target.name]: event.target.value,
        },
      };
    });
  }

  function onChange(event) {
    setValue((prevState) => {
      return {
        ...prevState,
        [event.target.name]: event.target.value,
      };
    });
  }

  function onAddressChange(event) {
    setAddress({
      ...value.address,
      [event.target.name]: event.target.value,
    });
  }

  function setAddress(address) {
    setValue((prevState) => {
      return {
        ...prevState,
        address,
      };
    });
  }

  async function toggleDisableLogin() {
    setSubmitting(true);
    let result = await dispatch(
      api.Patients.updatePatient({
        id: patient.id,
        patient: { ...value, login_disabled: !value.login_disabled },
      }),
      {
        errorTitle: 'Patient Details',
      },
    );
    setSubmitting(false);
    if (result) {
      setValue(result);
    }
  }

  async function toggleEnabled() {
    setSubmitting(true);

    const ep = patient.inactive
      ? api.Patients.updatePatient({ id: patient.id, ...patient, inactive: false })
      : api.Patients.deactivateAccount({ patientId: patient.id });

    const result = await dispatch(ep, {
      errorTitle: 'Patient Details',
    });

    setSubmitting(false);
    if (result) {
      setValue(result);
    }
  }

  async function resendEmailConfirmation() {
    setResendingEmailConfirmation(true);
    await dispatch(api.Authentication.sendEmailVerification({ id: patient.id }), {
      errorTitle: 'Email Confirmation',
    });
    setResendingEmailConfirmation(true);
  }

  async function save() {
    setSubmitting(true);
    let result = await dispatch(api.Patients.updatePatient({ id: patient.id, patient: value }), {
      errorTitle: 'Patient Details',
    });
    setSubmitting(false);
    if (result) setValue(result);
  }

  return (
    <>
      <View
        patient={patient}
        value={value}
        setValue={setValue}
        resendingEmailConfirmation={resendingEmailConfirmation}
        username={username}
        submitting={submitting}
        usernameNotFound={usernameNotFound}
        clinics={clinics}
        resendEmailConfirmation={resendEmailConfirmation}
        toggleEnabled={toggleEnabled}
        toggleDisableLogin={toggleDisableLogin}
        onEmergencyContactChange={onEmergencyContactChange}
        onAddressChange={onAddressChange}
        setAddress={setAddress}
        onChange={onChange}
        save={save}
        isPatient={ownRole === 'patient'}
        allowEdit={allowEdit}
        patientEdited={patientEdited}
        onShowUsername={ownRole === 'admin' ? onShowUsername : undefined}
        onGenerateCredentials={ownRole === 'admin' ? onGenerateCredentials : undefined}
      />
    </>
  );
};

PatientInfo.propTypes = {
  patient: PropTypes.object.isRequired,
  sendMessage: PropTypes.func,
};

export default PatientInfo;
